import moment from 'moment-timezone';
import { useEffect, useState } from 'react';

const LEAGUE_KICKOFF_HOUR = 19;
const CUP_KICKOFF_HOUR = 12;
const DAY_FORMAT = 'ddd, DD MMM YYYY';

export const TIMEZONE_STORAGE_KEY = 'tol-timezone';

export const TIMEZONE_OPTIONS = [
  { id: 'local', label: 'My computer' },
  { id: 'UTC', label: 'UTC' },
  { id: 'Europe/London', label: 'London' },
  { id: 'Europe/Berlin', label: 'Berlin / Paris' },
  { id: 'Europe/Istanbul', label: 'Istanbul' },
  { id: 'America/New_York', label: 'New York' },
  { id: 'America/Chicago', label: 'Chicago' },
  { id: 'America/Los_Angeles', label: 'Los Angeles' },
  { id: 'America/Sao_Paulo', label: 'São Paulo' },
  { id: 'Asia/Dubai', label: 'Dubai' },
  { id: 'Asia/Kolkata', label: 'Mumbai' },
  { id: 'Asia/Singapore', label: 'Singapore' },
  { id: 'Asia/Tokyo', label: 'Tokyo' },
  { id: 'Australia/Sydney', label: 'Sydney' },
];

const tzListeners = new Set();

const isValidZone = (value) =>
  Boolean(value && value !== 'local' && moment.tz.zone(value));

export const getTimezonePreference = () => {
  try {
    const stored = localStorage.getItem(TIMEZONE_STORAGE_KEY);
    if (!stored || stored === 'local') return 'local';
    if (isValidZone(stored)) return stored;
  } catch (error) {
    /* private mode */
  }
  return 'local';
};

export const getDisplayTimezone = () => {
  const preference = getTimezonePreference();
  if (preference !== 'local' && isValidZone(preference)) return preference;
  return moment.tz.guess() || 'UTC';
};

export const setDisplayTimezone = (next) => {
  const value = next === 'local' || !isValidZone(next) ? 'local' : next;
  try {
    localStorage.setItem(TIMEZONE_STORAGE_KEY, value);
  } catch (error) {
    /* private mode */
  }
  tzListeners.forEach((listener) => listener(getDisplayTimezone()));
};

export const useDisplayTimezone = () => {
  const [zone, setZone] = useState(getDisplayTimezone);
  useEffect(() => {
    tzListeners.add(setZone);
    return () => tzListeners.delete(setZone);
  }, []);
  return zone;
};

const inDisplayZone = (value) => {
  if (!value) return null;
  return value.clone().tz(getDisplayTimezone());
};

export const toLocalMoment = (value) => {
  if (!value) return null;
  const parsed = moment.utc(value);
  return parsed.isValid() ? inDisplayZone(parsed) : null;
};

export const formatLocal = (value, format = 'D MMM YYYY') => {
  const local = toLocalMoment(value);
  return local ? local.format(format) : '';
};

export const matchKickoff = (matchDate, { timezone, isCup } = {}) => {
  if (!matchDate) return null;
  const zone = timezone || 'UTC';
  const hour = isCup ? CUP_KICKOFF_HOUR : LEAGUE_KICKOFF_HOUR;
  // API match_date is a UTC midnight stamp for the fixture day. On western
  // servers that instant still falls on the previous local calendar day — and
  // league/cup kickoff is 19:00 / 12:00 on that local day. Using only the
  // YYYY-MM-DD slice shifted USW kickoffs one day late for Europe.
  const dayInZone = moment.utc(matchDate).tz(zone);
  if (!dayInZone.isValid()) return toLocalMoment(matchDate);
  const kickoff = moment.tz(
    `${dayInZone.format('YYYY-MM-DD')} ${String(hour).padStart(2, '0')}:00:00`,
    'YYYY-MM-DD HH:mm:ss',
    zone
  );
  return kickoff.isValid() ? inDisplayZone(kickoff) : toLocalMoment(matchDate);
};

export const formatMatchWhen = (
  matchDate,
  { timezone, isCup, dateFormat = 'MMM D', timeFormat = 'h:mm A' } = {}
) => {
  const local = matchKickoff(matchDate, { timezone, isCup });
  if (!local) return '';
  return `${local.format(dateFormat)} · ${local.format(timeFormat)}`;
};

export const formatMatchDay = (
  matchDate,
  { timezone, isCup, format = DAY_FORMAT } = {}
) => {
  const local = matchKickoff(matchDate, { timezone, isCup });
  return local ? local.format(format) : '';
};

export const localToday = (format = DAY_FORMAT) =>
  moment().tz(getDisplayTimezone()).format(format);
